import { START_LOCATION, createRouter, createWebHistory } from 'vue-router'
import { toast } from 'vue3-toastify'

import { canNavigate } from '@/@layouts/plugins/casl'
import { clearReloadGuard, isChunkLoadErrorMessage, reloadOnce } from '@/plugins/chunkLoadRecovery'
import { useEntityStore } from '@/stores/entity'
import { useUserStore } from '@/stores/user'
import { installCampaignArrivalTracking, installScreenTracking } from '@/utils/instrumentation'

import { loginReturnPath, resolveGoLink } from './goLinks'
import provider from './provider'
import { installSafeResolve } from './safeResolve'
import settings from './settings'
import { isOtherHospital, isUserLoggedIn, rootRedirect } from './utils'
import vector from './vector'

import routes from '~pages'

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    // ℹ️ We are redirecting to different pages based on role.
    // NOTE: Role is just for UI purposes. ACL is based on abilities.
    {
      path: '/',
      redirect: rootRedirect,
    },
    {
      path: '/reset-password/:token',
      name: 'auth-reset-password',
      component: () => import('@/pages/reset-password.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
        redirectIfLoggedIn: true,
      },
      props: true,
    },
    {
      path: '/ssdi/qualify/:token',
      name: 'ssdi-patient-form',
      component: () => import('@/pages/ssdi/QualificationForm.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
      },
    },
    {
      path: '/ssdi/apply/:providerUuid',
      name: 'ssdi-provider-apply',
      component: () => import('@/pages/ssdi/ProviderApplyForm.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
        action: 'read',
        subject: 'Auth',
      },
    },
    {
      path: '/onboarding/:token',
      name: 'onboarding-portal',
      component: () => import('@/pages/onboarding/Portal.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
      },
    },
    {
      path: '/apply/medicaid/:token',
      name: 'medicaid-apply',
      component: () => import('@/pages/medicaid/Apply.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
      },
    },
    {
      path: '/apply/medicaid/provider/:providerUuid',
      name: 'medicaid-provider-search',
      component: () => import('@/pages/medicaid/ProviderSearch.vue'),
      meta: {
        layout: 'blank',
        resource: 'Public',
      },
    },
    {
      // Hospital-neutral links for product-update emails (VH-3477). Always redirects, so it
      // needs no component.
      path: '/go/:feature',
      name: 'go',
      beforeEnter: resolveGoLink,
      meta: {
        layout: 'blank',
        resource: 'Provider',
      },
    },
    ...routes,
    ...vector,
    ...provider,
    ...settings,

    {
      path: '/not-authorized',
      name: 'not-authorized',
      component: () => import('@/pages/403.vue'),
      meta: {
        layout: 'blank',
        resource: 'public',
      },
    },

    // ✅ Catch-all 404 route must be LAST
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      component: () => import('@/pages/404.vue'), // or a custom 404.vue page
      meta: {
        layout: 'blank',
        resource: 'public',
      },
    },
  ],
}) // Adjust the path based on your file structure

// Must run before anything mounts: still-mounted provider-scoped links re-resolve
// synchronously when a navigation lands on a route without their params (e.g.
// logout -> /login), too early for any unmount or v-if to prevent it. See
// safeResolve.ts for the full mechanism. (VH-2283)
installSafeResolve(router)

// Screen views and dwell time (VH-2670). Registers an afterEach guard, so it
// only ever observes navigations that actually completed — a redirect from
// one of the guards below does not end a dwell on a screen nobody left.
installScreenTracking(router)

// Arrivals from product-update emails (VH-2935).
installCampaignArrivalTracking(router)

// Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
// eslint-disable-next-line sonarjs/cognitive-complexity
router.beforeEach(async to => {
  if (
    window.location.protocol !== 'https:'
    && import.meta.env.VITE_APP_URL.startsWith('https:')
    && import.meta.env.VITE_NODE_ENV !== 'local'
  ) {
    window.location.replace(import.meta.env.VITE_APP_URL + to.fullPath)

    return false // ✅ stop navigation after redirect
  }

  if (isUserLoggedIn()) {
    const user = useUserStore()
    const entity = useEntityStore()

    if (!user.data.uuid && (to.meta.resource || '').toLowerCase() !== 'public') {
      await user
        .get()
        .then(() => {
          if (
            (to.meta.resource || '').toLowerCase() !== 'public'
            && localStorage.getItem('isAuthenticated')
          ) {
            if (!to.path)
              return null

            if (to.path && to.path.startsWith('/vector/') && user.data?.access.vector)
              entity.set({ vector: true })
          }
        })
        .catch(error => {
          console.error('Error fetching user data:', error)
        })
    }

    if (to.meta.redirectIfLoggedIn)
      return { name: 'dashboard-interstitial' }

    // for not-authorized
    if (!canNavigate(to)) {
      // VH-3574: someone taken off a hospital's inbox list can still follow an older digest email
      // or notification here; say why they landed somewhere else.
      if (to.name === 'iv-inbox')
        toast.info('You don\'t have access to the Insurance Inbox. Ask your admin if you need it.')

      return { path: '/' }
    }

    // The API refuses another hospital's data (403); say so instead of showing a
    // page whose every request fails.
    if (isOtherHospital(to.params.provider_uuid, user.providers.map(p => p.uuid), !!user.data?.access?.vector))
      return { name: 'not-authorized' }
  } else if (!to.meta.redirectIfLoggedIn && (to.meta.resource || '').toLowerCase() !== 'public') {
    let redirectPath: string | undefined = loginReturnPath(to.fullPath)
    if (to.name === 'dashboard-interstitial' || to.name === 'index')
      redirectPath = undefined

    return { name: 'login', query: { to: redirectPath } }
  }
})

// When the user's tab is open across a deploy, lazy-loaded route chunks the
// old build references are no longer on the CDN. Vue Router surfaces this as
// "Failed to fetch dynamically imported module" and the navigation never
// completes. Hard-reload to fetch the fresh index.html and current chunk hashes.
// The reload is one-shot (see chunkLoadRecovery.ts) so a chunk that is really
// missing cannot loop; the guard re-arms after the next completed navigation.
router.onError((error, to) => {
  if (!isChunkLoadErrorMessage(error?.message ?? ''))
    return

  if (!navigator.onLine) {
    const banner = document.createElement('div')

    banner.textContent = 'Unable to load page. Check your connection and refresh.'
    Object.assign(banner.style, {
      position: 'fixed',
      bottom: '1rem',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#1a1a2e',
      color: '#fff',
      padding: '0.75rem 1.25rem',
      borderRadius: '0.5rem',
      zIndex: '9999',
      fontSize: '0.875rem',
    })
    document.body.appendChild(banner)

    return
  }

  reloadOnce(() => {
    window.location.href = to.fullPath
  })
})

// Re-arm the chunk-load reload once the user has navigated successfully. The
// initial navigation is skipped: it completes right after the reload itself,
// and re-arming there would let a boot-time chunk failure reload forever.
router.afterEach((_to, from, failure) => {
  if (!failure && from !== START_LOCATION)
    clearReloadGuard()
})

export default router
